import React, { useState, Fragment } from 'react';
import { Button } from '../ui/Button';
import { AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, CoinsIcon } from 'lucide-react';
import { DocumentUploadSelector } from './DocumentUploadSelector';
interface ClientInvoices {
  id: string;
  clienteNome: string;
  clientePiva: string;
  importoTotale: number;
  fatture: {
    id: string;
    numero: string;
    data: string;
    importo: number;
  }[];
  documentiRichiesti: {
    fatture: boolean;
    contratto: boolean;
    solleciti: boolean;
    altro: boolean;
  };
  praticaCompleta: boolean;
  complessita: 'bassa' | 'media' | 'alta';
}
interface UnpaidInvoicesTableProps {
  searchTerm: string;
}
export function UnpaidInvoicesTable({
  searchTerm
}: UnpaidInvoicesTableProps) {
  const [sortField, setSortField] = useState<string>('clienteNome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [showCreditConfirmation, setShowCreditConfirmation] = useState<string | null>(null);
  const [clientData, setClientData] = useState<ClientInvoices[]>([{
    id: 'client-1',
    clienteNome: 'TechSolutions Spa',
    clientePiva: '98765432101',
    importoTotale: 12000,
    fatture: [{
      id: 'inv-1',
      numero: '2023/015',
      data: '2023-02-28',
      importo: 12000
    }],
    documentiRichiesti: {
      fatture: true,
      contratto: false,
      solleciti: true,
      altro: false
    },
    praticaCompleta: false,
    complessita: 'bassa'
  }, {
    id: 'client-2',
    clienteNome: 'Logistica Express Srl',
    clientePiva: '78901234561',
    importoTotale: 16200,
    fatture: [{
      id: 'inv-2',
      numero: '2023/022',
      data: '2023-03-20',
      importo: 4200
    }, {
      id: 'inv-3',
      numero: '2023/028',
      data: '2023-04-10',
      importo: 12000
    }],
    documentiRichiesti: {
      fatture: true,
      contratto: true,
      solleciti: false,
      altro: false
    },
    praticaCompleta: false,
    complessita: 'media'
  }, {
    id: 'client-3',
    clienteNome: 'Edilizia Moderna Srl',
    clientePiva: '23456789011',
    importoTotale: 6500,
    fatture: [{
      id: 'inv-4',
      numero: '2023/042',
      data: '2023-05-05',
      importo: 6500
    }],
    documentiRichiesti: {
      fatture: true,
      contratto: true,
      solleciti: true,
      altro: true
    },
    praticaCompleta: true,
    complessita: 'alta'
  }, {
    id: 'client-4',
    clienteNome: 'Arredamenti Design Srl',
    clientePiva: '34567890122',
    importoTotale: 8750,
    fatture: [{
      id: 'inv-5',
      numero: '2023/051',
      data: '2023-05-15',
      importo: 8750
    }],
    documentiRichiesti: {
      fatture: false,
      contratto: false,
      solleciti: false,
      altro: false
    },
    praticaCompleta: false,
    complessita: 'media'
  }]);
  // Filtraggio per termine di ricerca
  const filteredClients = clientData.filter(client => {
    return client.clienteNome.toLowerCase().includes(searchTerm.toLowerCase()) || client.clientePiva.includes(searchTerm) || client.fatture.some(f => f.numero.toLowerCase().includes(searchTerm.toLowerCase()));
  });
  // Ordinamento dei clienti
  const sortedClients = [...filteredClients].sort((a, b) => {
    if (sortField === 'clienteNome') {
      return sortDirection === 'asc' ? a.clienteNome.localeCompare(b.clienteNome) : b.clienteNome.localeCompare(a.clienteNome);
    }
    if (sortField === 'importoTotale') {
      return sortDirection === 'asc' ? a.importoTotale - b.importoTotale : b.importoTotale - a.importoTotale;
    }
    if (sortField === 'documentiMancanti') {
      const aMancanti = Object.values(a.documentiRichiesti).filter(v => !v).length;
      const bMancanti = Object.values(b.documentiRichiesti).filter(v => !v).length;
      return sortDirection === 'asc' ? aMancanti - bMancanti : bMancanti - aMancanti;
    }
    return 0;
  });
  // Funzione per gestire il cambio di ordinamento
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Gestione dell'espansione delle righe
  const toggleRowExpanded = (id: string) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };
  // Formattazione dell'importo in euro
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  // Renderizza l'icona di ordinamento
  const renderSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-763" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-764" />;
  };
  // Calcola il numero di documenti mancanti
  const getDocumentiMancanti = (client: ClientInvoices) => {
    return Object.values(client.documentiRichiesti).filter(v => !v).length;
  };
  // Aggiorna lo stato dei documenti
  const handleDocumentUpload = (clientId: string, documentType: keyof ClientInvoices['documentiRichiesti']) => {
    setClientData(prevData => prevData.map(client => {
      if (client.id === clientId) {
        const updatedDocs = {
          ...client.documentiRichiesti,
          [documentType]: true
        };
        // Controlla se tutti i documenti sono stati caricati
        const praticaCompleta = Object.values(updatedDocs).every(v => v);
        return {
          ...client,
          documentiRichiesti: updatedDocs,
          praticaCompleta
        };
      }
      return client;
    }));
  };
  // Gestisce il click sul pulsante "Invia al Legale"
  const handleInviaAlLegale = (clientId: string) => {
    setShowCreditConfirmation(clientId);
  };
  // Conferma l'invio al legale
  const confirmInviaAlLegale = (clientId: string) => {
    // Qui andresti a modificare lo stato del cliente
    // e a decrementare i crediti disponibili
    setShowCreditConfirmation(null);
  };
  // Calcola i crediti richiesti in base alla complessità
  const getRequiredCredits = (complessita: ClientInvoices['complessita']) => {
    switch (complessita) {
      case 'bassa':
        return 150;
      case 'media':
        return 200;
      case 'alta':
        return 300;
      default:
        return 150;
    }
  };
  return <div className="overflow-x-auto" data-id="element-765">
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" data-id="element-766">
        <div className="flex" data-id="element-767">
          <div className="flex-shrink-0" data-id="element-768">
            <AlertCircleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" data-id="element-769" />
          </div>
          <div className="ml-3" data-id="element-770">
            <p className="text-sm text-yellow-700" data-id="element-771">
              Questa sezione mostra i clienti con partite scadute che non hanno
              un piano di rateazione attivo. È necessario caricare tutti i
              documenti richiesti prima di poter inviare la pratica al legale.
              <span className="ml-1 font-semibold" data-id="element-772">
                L'invio al legale richiede da 150 a 300 crediti in base alla
                complessità della pratica.
              </span>
            </p>
          </div>
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200" data-id="element-773">
        <thead className="bg-gray-50" data-id="element-774">
          <tr data-id="element-775">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('clienteNome')} data-id="element-776">
              <div className="flex items-center" data-id="element-777">
                Cliente
                {renderSortIcon('clienteNome')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('importoTotale')} data-id="element-778">
              <div className="flex items-center" data-id="element-779">
                Importo Totale
                {renderSortIcon('importoTotale')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-780">
              Fatture
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('documentiMancanti')} data-id="element-781">
              <div className="flex items-center" data-id="element-782">
                Documenti
                {renderSortIcon('documentiMancanti')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-783">
              Complessità
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-784">
              Azioni
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-785">
          {sortedClients.length === 0 ? <tr data-id="element-786">
              <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-787">
                Nessuna partita scaduta trovata
              </td>
            </tr> : sortedClients.map(client => <Fragment key={client.id} data-id="element-788">
                <tr className="hover:bg-gray-50" data-id="element-789">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-790">
                    <div className="text-sm font-medium text-gray-900" data-id="element-791">
                      {client.clienteNome}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-792">
                      {client.clientePiva}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-793">
                    <div className="text-sm font-medium text-red-600" data-id="element-794">
                      {formatCurrency(client.importoTotale)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-795">
                    <button type="button" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-900" onClick={() => toggleRowExpanded(client.id)} data-id="element-796">
                      <FileTextIcon className="h-4 w-4 mr-1" data-id="element-797" />
                      Vedi {client.fatture.length} fatture
                      {expandedRows.includes(client.id) ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-798" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-799" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-800">
                    <DocumentUploadSelector clientId={client.id} documentiRichiesti={client.documentiRichiesti} onDocumentUpload={handleDocumentUpload} data-id="element-801" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-802">
                    <div className={`text-sm font-medium ${client.complessita === 'alta' ? 'text-red-600' : client.complessita === 'media' ? 'text-amber-600' : 'text-green-600'}`} data-id="element-803">
                      {client.complessita === 'alta' ? 'Alta' : client.complessita === 'media' ? 'Media' : 'Bassa'}
                      <span className="text-xs ml-1 text-gray-500" data-id="element-804">
                        ({getRequiredCredits(client.complessita)} crediti)
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-805">
                    {client.praticaCompleta ? showCreditConfirmation === client.id ? <div className="flex items-center justify-end space-x-2" data-id="element-806">
                          <div className="flex items-center text-amber-600" data-id="element-807">
                            <CoinsIcon className="h-4 w-4 mr-1" data-id="element-808" />
                            <span data-id="element-809">
                              {getRequiredCredits(client.complessita)} crediti
                            </span>
                          </div>
                          <button onClick={() => confirmInviaAlLegale(client.id)} className="bg-[#1A3E5C] text-white px-2 py-1 rounded text-xs" data-id="element-810">
                            Conferma
                          </button>
                          <button onClick={() => setShowCreditConfirmation(null)} className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs" data-id="element-811">
                            Annulla
                          </button>
                        </div> : <Button onClick={() => handleInviaAlLegale(client.id)} data-id="element-812">
                          Invia al Legale
                        </Button> : <Button disabled data-id="element-813">Invia al Legale</Button>}
                  </td>
                </tr>
                {expandedRows.includes(client.id) && <tr data-id="element-814">
                    <td colSpan={6} className="px-6 py-4 bg-gray-50" data-id="element-815">
                      <div className="text-sm text-gray-900 font-medium mb-2" data-id="element-816">
                        Elenco Fatture
                      </div>
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-817">
                        <thead className="bg-gray-100" data-id="element-818">
                          <tr data-id="element-819">
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-820">
                              Numero
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-821">
                              Data
                            </th>
                            <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-822">
                              Importo
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-823">
                          {client.fatture.map(fattura => <tr key={fattura.id} className="hover:bg-gray-50" data-id="element-824">
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-825">
                                {fattura.numero}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-826">
                                {new Date(fattura.data).toLocaleDateString('it-IT')}
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-827">
                                {formatCurrency(fattura.importo)}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </td>
                  </tr>}
              </Fragment>)}
        </tbody>
      </table>
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-828">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-829">
          <div data-id="element-830">
            <p className="text-sm text-gray-700" data-id="element-831">
              Visualizzazione{' '}
              <span className="font-medium" data-id="element-832">{sortedClients.length}</span> di{' '}
              <span className="font-medium" data-id="element-833">{clientData.length}</span> clienti
              con partite scadute
            </p>
          </div>
        </div>
      </div>
    </div>;
}